/* eslint-disable canonical/filename-match-exported */

import HomeQuery, {
  type HomePageQuery,
} from '@/__generated__/relay/HomePageQuery.graphql.js';
import { type UserProfile_userProfile$key } from '@/__generated__/relay/UserProfile_userProfile.graphql.js';
import { MiniInquiryButton } from '@/components/InquiryButton/MiniInquiryButton.js';
import { MissingResource } from '@/components/MissingResource/MissingResource.js';
import { PageHeadFragment } from '@/components/PageHead/PageHead.js';
import { TemplateRenderer } from '@/components/TemplateRenderer/TemplateRenderer.js';
import { UserProfileFragment } from '@/documents/fragments/UserProfile.js';
import HOME_PAGE_QUERY from '@/documents/queries/HomePage.js';
import { useIPFragment } from '@/hooks/useIPFragment.js';
import { getPreloadedQuery } from '@/services/relay/network.js';
import { buildPortfolioMetaTitle } from '@/utilities/buildPortfolioMetaTitle.js';
import { buildUserRoles } from '@/utilities/buildUserRoles.js';
import { isPlaywrightTestEnvironment } from '@/utilities/isPlaywrightTestEnvironment.js';
import { redirectInvalidUsername } from '@/utilities/redirectInvalidUsername.js';
import { resolveGraphQLResponse } from '@/utilities/resolveGraphQLResponse.js';
import { resolveMetadaUrl } from '@/utilities/resolveMetadataUrl.js';
import { resolveUsername } from '@/utilities/resolveUsername.js';
import { type GetServerSidePropsContext } from 'next';
import dynamic from 'next/dynamic';
import { type PreloadedQuery, usePreloadedQuery } from 'react-relay';

const PageProviders = dynamic(
  async () => await import('@/components/PageProviders/PageProviders.js'),
  {
    ssr: false,
  },
);

const Home = ({
  isCustomDomain,
  isPlaywright,
  queryRefs,
  ...props
}: {
  readonly isCustomDomain: boolean;
  readonly isPlaywright: boolean;
  readonly queryRefs: { query: PreloadedQuery<HomePageQuery> };
  readonly username: string;
}) => {
  const queryRef = usePreloadedQuery<HomePageQuery>(
    HOME_PAGE_QUERY,
    queryRefs.query,
  );

  const profile = useIPFragment<UserProfile_userProfile$key>(
    UserProfileFragment,
    queryRef?.userProfileByUsername ?? null,
  );

  if (!queryRef?.userProfileByUsername) {
    return <MissingResource />;
  }

  const { userProfileByUsername } = queryRef;
  const templateProps = { ...props, queryRef: userProfileByUsername };

  // Build the link-preview title from the same aggregated roles (services +
  // projects) shown on the page so removed roles never surface in the preview.
  const titleOverride = profile
    ? buildPortfolioMetaTitle({
        firstName: profile.firstName,
        lastName: profile.lastName,
        roles: buildUserRoles(profile.tags),
      })
    : null;

  return userProfileByUsername ? (
    <>
      <PageHeadFragment
        isCustomDomain={isCustomDomain}
        queryNode={queryRef}
        titleOverride={titleOverride}
      />
      <PageProviders
        analyticsResourceData={null}
        isPlaywright={isPlaywright}
        templateQueryRef={queryRef}
        userQueryRef={queryRef.userProfileByUsername}
        {...props}
      >
        <TemplateRenderer
          page="Home"
          {...templateProps}
        />
        <MiniInquiryButton />
      </PageProviders>
    </>
  ) : (
    <MissingResource />
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  const isPlaywright = isPlaywrightTestEnvironment(context);
  const { isCustomDomain, username } = await resolveUsername(context);

  if (!username) {
    return redirectInvalidUsername(context);
  }

  const pageUrl = resolveMetadaUrl(context, username);
  const pageQuery = await getPreloadedQuery(HomeQuery, {
    url: pageUrl,
    username,
  });

  const queryData = resolveGraphQLResponse(pageQuery.response);
  if (!queryData.data?.['userProfileByUsername']) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      isCustomDomain,
      isPlaywright,
      preloadedQueries: {
        query: pageQuery,
      },
      username,
    },
  };
};

export default Home;
